import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building, Plus, RefreshCw, Trash2, Star, MapPin, Hash, Phone, Mail, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const MesStructures = ({ user, onSwitched }) => {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    nom_entreprise: '', siret: '', adresse: '', code_postal: '', ville: '',
    telephone: '', email_contact: '', gerant_genre: 'M.', gerant_prenom: '', gerant_nom: '',
    forme_juridique: 'SAS',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/me/structures');
      setStructures(res.data || []);
    } catch (e) {
      toast.error('Impossible de charger vos structures');
    } finally { setLoading(false); }
  };

  const switchTo = async (structureId) => {
    try {
      const res = await axios.post('/me/switch-structure', { structure_id: structureId });
      const { access_token, user: targetUser } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(targetUser));
      toast.success(`Structure "${targetUser.nom || 'principale'}" activée`);
      if (onSwitched) onSwitched();
      window.location.href = '/';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du changement de structure');
    }
  };

  const create = async () => {
    if (!form.nom_entreprise.trim()) { toast.error('Nom obligatoire'); return; }
    setCreating(true);
    try {
      await axios.post('/me/structures', form);
      toast.success('Structure créée');
      setShowCreate(false);
      setForm({ nom_entreprise: '', siret: '', adresse: '', code_postal: '', ville: '', telephone: '', email_contact: '', gerant_genre: 'M.', gerant_prenom: '', gerant_nom: '', forme_juridique: 'SAS' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreating(false); }
  };

  const remove = async (s) => {
    if (s.is_primary) { toast.error('Impossible de supprimer la structure principale'); return; }
    if (!window.confirm(`Délier la structure "${s.nom_entreprise}" ? Les données restent en base.`)) return;
    try {
      await axios.delete(`/me/structures/${s.user_id}`);
      toast.success('Structure déliée');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><div className="animate-spin h-10 w-10 border-b-2 border-[#8DC63F] rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="mes-structures-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-1">Mes Structures</h1>
          <p className="text-slate-600 text-sm">Gérez plusieurs structures juridiques (SIRET) avec un seul compte.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#8DC63F] hover:bg-[#7ab036] text-white" data-testid="btn-add-structure">
          <Plus className="h-4 w-4 mr-2" /> Ajouter une structure
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {structures.map(s => (
          <div key={s.user_id} className={`bg-white rounded-lg p-5 border-2 shadow-sm ${s.is_primary ? 'border-[#8DC63F]' : 'border-slate-200'}`} data-testid={`structure-card-${s.user_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-[#8DC63F]" />
                <h3 className="font-display font-semibold text-lg text-[#0F172A]">{s.nom_entreprise}</h3>
              </div>
              {s.is_primary && (
                <span className="flex items-center gap-1 text-xs bg-[#8DC63F]/10 text-[#8DC63F] px-2 py-1 rounded-full font-medium">
                  <Star className="h-3 w-3" /> Principale
                </span>
              )}
            </div>
            <div className="space-y-1.5 text-sm text-slate-600 mb-4">
              {s.siret && <p className="flex items-center gap-2"><Hash className="h-4 w-4 text-slate-400" /> SIRET : {s.siret}</p>}
              {(s.adresse || s.ville) && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {[s.adresse, s.code_postal, s.ville].filter(Boolean).join(' ')}</p>}
              {(s.gerant_prenom || s.gerant_nom) && <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> Gérant : {[s.gerant_prenom, s.gerant_nom].filter(Boolean).join(' ')}</p>}
              {s.email && !s.email.includes('@fisl-multi.local') && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {s.email}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              {s.user_id !== user?.user_id && (
                <Button size="sm" onClick={() => switchTo(s.user_id)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`btn-switch-${s.user_id}`}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Activer cette structure
                </Button>
              )}
              {!s.is_primary && (
                <Button size="sm" variant="outline" onClick={() => remove(s)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`btn-remove-${s.user_id}`}>
                  <Trash2 className="h-4 w-4 mr-2" /> Délier
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter une structure</DialogTitle>
            <DialogDescription>Créez une nouvelle structure juridique liée à votre compte.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="md:col-span-2">
              <Label>Nom de l'entreprise *</Label>
              <Input value={form.nom_entreprise} onChange={e => setForm({ ...form, nom_entreprise: e.target.value })} placeholder="Nautic Club 17" data-testid="form-nom" />
            </div>
            <div>
              <Label>SIRET</Label>
              <Input value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} placeholder="12345678900012" data-testid="form-siret" />
            </div>
            <div>
              <Label>Forme juridique</Label>
              <Input value={form.forme_juridique} onChange={e => setForm({ ...form, forme_juridique: e.target.value })} placeholder="SAS, SARL..." />
            </div>
            <div className="md:col-span-2">
              <Label>Adresse</Label>
              <Input value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} placeholder="Rue, numéro" />
            </div>
            <div>
              <Label>Code postal</Label>
              <Input value={form.code_postal} onChange={e => setForm({ ...form, code_postal: e.target.value })} placeholder="17110" />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} placeholder="Royan" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} placeholder="0546..." />
            </div>
            <div>
              <Label>Email de contact</Label>
              <Input value={form.email_contact} onChange={e => setForm({ ...form, email_contact: e.target.value })} placeholder="contact@..." />
            </div>
            <div>
              <Label>Genre du gérant</Label>
              <select value={form.gerant_genre} onChange={e => setForm({ ...form, gerant_genre: e.target.value })} className="w-full h-10 rounded-md border border-input px-3">
                <option value="M.">M.</option>
                <option value="Mme">Mme</option>
              </select>
            </div>
            <div>
              <Label>Prénom du gérant</Label>
              <Input value={form.gerant_prenom} onChange={e => setForm({ ...form, gerant_prenom: e.target.value })} placeholder="Patrick" />
            </div>
            <div className="md:col-span-2">
              <Label>Nom du gérant</Label>
              <Input value={form.gerant_nom} onChange={e => setForm({ ...form, gerant_nom: e.target.value })} placeholder="GAPIN" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Annuler</Button>
            <Button onClick={create} disabled={creating} className="bg-[#8DC63F] hover:bg-[#7ab036] text-white" data-testid="btn-create-structure-confirm">
              {creating ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MesStructures;
